import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ModalDefaultDraggable } from '../../../molecules';
import { useUpdateWikiKeyMutation } from '../../../redux/rtkQuery/apiWikiKeysSlice';
import RichTextEditor, { isRichTextEmpty } from '../SharedComponents/RichTextEditor';

type Props = {
  row: any;
  onClose: () => void;
};

const EditCredentialModal = ({ row, onClose }: Props) => {
  const [login, setLogin] = useState(row.username || '');
  const [key, setKey] = useState(row.password || '');
  const [notes, setNotes] = useState(row.notes || '');
  const [error, setError] = useState('');

  const [updateWikiKey] = useUpdateWikiKeyMutation();

  const clientDisplay = (() => {
    const name = [row.firstName, row.lastName].filter(Boolean).join(' ');
    return [name, row.companyName].filter(Boolean).join(' - ') || '';
  })();

  useEffect(() => {
    setError('');
  }, [login, key, notes]);

  const onUpdate = async () => {
    if (!login || !key) {
      setError('Login and Key are required');
      return;
    }

    const formData = new FormData();
    formData.append('username', login);
    formData.append('password', key);
    formData.append('notes', isRichTextEmpty(notes) ? '' : notes);

    try {
      await updateWikiKey({ id: row.keyId, frm: formData }).unwrap();
      onClose();
    } catch (e) {
      setError('Failed to update credential');
    }
  };

  return (
    <StyledModal onClose={onClose} title="Edit Credential">
      <Form>
        {/* EA-1200: show the Client (read-only, gray) so the edit screen has the
            same context as Add New Credential. */}
        <FieldLabel>Client</FieldLabel>
        <StaticValue>{clientDisplay}</StaticValue>

        <FieldLabel>Jurisdiction</FieldLabel>
        <StaticValue>{row.jurisdictionName}</StaticValue>

        <FieldLabel>Login<RequiredStar>*</RequiredStar></FieldLabel>
        <Input
          type="text"
          value={login}
          onChange={e => setLogin(e.target.value)}
        />

        <FieldLabel>Key<RequiredStar>*</RequiredStar></FieldLabel>
        <Input
          type="text"
          value={key}
          onChange={e => setKey(e.target.value)}
        />

        <FieldLabel>Notes</FieldLabel>
        <RichTextEditor
          value={notes}
          onChange={setNotes}
          placeholder="Add notes for this credential..."
        />

        {error && <ErrorText>{error}</ErrorText>}

        <UpdateButton onClick={onUpdate}>UPDATE</UpdateButton>
      </Form>
    </StyledModal>
  );
};

export default EditCredentialModal;

const StyledModal = styled(ModalDefaultDraggable)`
  overflow-y: auto;
  text-align: center;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 20px 20px;
  gap: 6px;
  text-align: left;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--primary);
  margin-top: 10px;
`;

// EA-1200: red required indicator for mandatory fields.
const RequiredStar = styled.span`
  color: var(--danger-500, #dc2626);
  margin-left: 2px;
`;

const StaticValue = styled.div`
  padding: 8px;
  font-size: 13px;
  color: var(--mainText);
  background-color: var(--disabledButton);
  border: 1px solid var(--border);
  border-radius: 4px;
`;

const Input = styled.input`
  width: 100%;
  height: 36px;
  padding: 4px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
`;

const ErrorText = styled.div`
  color: red;
  font-size: 12px;
  text-align: center;
  margin-top: 4px;
`;

const UpdateButton = styled.button`
  background-color: var(--primary);
  color: #fff;
  border: none;
  padding: 8px 32px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 3px;
  margin-top: 16px;
  align-self: center;

  &:hover {
    opacity: 0.9;
  }
`;
